import { useState, useEffect } from "react";
import {
  HttpError,
  useDataProvider,
  useNotification,
  useShow,
} from "@refinedev/core";
import { z } from "zod";
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Copy,
  Check,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useDocumentTitle } from "@refinedev/react-router";

const providerSchema = z.object({
  id: z.string(),
  provider: z.string(),
  logo: z.string(),
  url: z.string().nullish(),
  headers: z.any(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

type ProviderData = z.infer<typeof providerSchema>;

// export const ProvidersShow = () => {
//     return <HeadlessInferencer />;
// };

export const ProvidersShow = () => {
  const {
    query: { data: queryResult },
    showId,
  } = useShow<ProviderData, HttpError>();
  const [data, setData] = useState<ProviderData | undefined>();
  const [showHeaders, setShowHeaders] = useState(false);
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [reauthKey, setReauthKey] = useState("");
  const [isReauthenticating, setIsReauthenticating] = useState(false);
  const { custom } = useDataProvider()();
  const setTitle = useDocumentTitle();
  const { open } = useNotification();

  useEffect(() => {
    if (queryResult) {
      const data = providerSchema.parse(queryResult);
      setData(data);
      setTitle("Provider | " + data.provider);
    }
  }, [queryResult]);

  const handleAuthenticate = () => {
    if (password === "admin123" || password === "headers") {
      setIsAuthenticated(true);
      setShowHeaders(true);
    } else {
      alert("Incorrect password!");
    }
  };

  const handleReauthenticate = async () => {
    if (!reauthKey.trim()) {
      alert("Please enter a reauthentication key");
      return;
    }

    setIsReauthenticating(true);

    // call api
    const response = await custom?.({
      url: `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/bots-authentication`,
      method: "post",
      payload: {
        provider: data!.id,
        authenticationMethod: "magicLink",
        url: reauthKey,
      },
    });

    setIsReauthenticating(false);
    setReauthKey("");

    open?.({
      message:
        "Provider reauthenticated successfully! New session token generated.",
      type: "success",
    });
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {data && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <img
                  src={data.logo || "/placeholder.svg"}
                  alt={`${data.provider} logo`}
                  className="h-8 rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div>
                  <CardTitle className="text-xl">{data.id}</CardTitle>
                  <CardDescription>Provider: {data.provider}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    URL
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                      {data.url}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(data?.url ?? "", "url")}
                    >
                      {copiedField === "url" ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Provider ID
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                      {data.id}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(data.id, "id")}
                    >
                      {copiedField === "id" ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Created At
                  </label>
                  <div className="p-2 bg-muted rounded text-sm">
                    {formatDate(data.createdAt)}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Updated At
                  </label>
                  <div className="p-2 bg-muted rounded text-sm">
                    {formatDate(data.updatedAt)}
                  </div>
                </div>
              </div>

              {/* Reauthentication Section */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <RefreshCw className="w-5 h-5  text-blue-400" />
                  <h3 className="text-lg font-semibold">
                    Provider Reauthentication
                  </h3>
                  <Badge variant="secondary">Admin Action</Badge>
                </div>

                <div className="space-y-4 p-4 border rounded-lg border-blue-800">
                  <p className="text-sm text-muted-foreground">
                    Trigger a reauthentication with the provider to refresh
                    session tokens and credentials.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter reauthentication key..."
                      value={reauthKey}
                      onChange={(e) => setReauthKey(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleReauthenticate()
                      }
                      disabled={isReauthenticating}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleReauthenticate}
                      disabled={isReauthenticating || !reauthKey.trim()}
                    >
                      {isReauthenticating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Authenticating...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reauthenticate
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This will generate a new session token and update the
                    provider credentials.
                  </p>
                </div>
              </div>

              {/* Protected Headers Section */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-destructive" />
                  <h3 className="text-lg font-semibold">Protected Headers</h3>
                  <Badge variant="destructive">Sensitive Data</Badge>
                </div>

                {!isAuthenticated ? (
                  <div className="space-y-4 p-4 border border-destructive/20 rounded-lg  bg-destructive/10">
                    <p className="text-sm text-muted-foreground">
                      This section contains sensitive authentication headers.
                      Please enter the password to view.
                    </p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password..."
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleAuthenticate()
                          }
                          className="pr-10"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <Button onClick={handleAuthenticate}>
                        <Unlock className="w-4 h-4 mr-2" />
                        Unlock
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600">
                      <Unlock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Headers unlocked
                      </span>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-muted-foreground">
                        Cookie
                      </label>
                      <div className="flex gap-2">
                        <code className="flex-1 p-3 bg-muted rounded text-xs font-mono break-all max-h-32 overflow-y-auto">
                          {data.headers.Cookie}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(data.headers.Cookie, "cookie")
                          }
                          className="shrink-0"
                        >
                          {copiedField === "cookie" ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsAuthenticated(false);
                        setShowHeaders(false);
                        setPassword("");
                      }}
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Lock Headers
                    </Button>
                  </div>
                )}
              </div>

              {/* Raw JSON Toggle */}
              <div className="border-t pt-6">
                <details className="space-y-2">
                  <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                    View Raw JSON
                  </summary>
                  <pre className="p-4 bg-muted rounded text-xs font-mono overflow-x-auto">
                    {JSON.stringify({ ...data, headers: null }, null, 2)}
                  </pre>
                </details>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
